/*
 * Copyright (c) 2019-2021 LogMeIn
 * All Rights Reserved Worldwide.
 *
 * THIS PROGRAM IS CONFIDENTIAL AND PROPRIETARY TO LOGMEIN
 * AND CONSTITUTES A VALUABLE TRADE SECRET.
 */

import React from 'react';
import { defineMessages, useIntl } from 'react-intl';
import {
  CheckIcon,
  CopyActiveIcon,
  Flex,
  TextField,
  useClipboard,
} from '@getgo/chameleon-chakra-ui';
import { StyledButton } from '../Buttons/StyledButton';
import { getCurrentUrl } from '../../services/NavigatorService';
import { Spacing } from '../../themes/GoToTheme';
import { GoToLink } from '../Link/GoToLink';
import { GoToText } from '../Texts/GoToText';
import {
  installGoogleChromeUrl,
  installMicrosoftEdgeUrl,
} from '../../services/UrlService';
import {
  useSendTelemetry,
  TelemetryComponent,
  TelemetryAction,
  TelemetryState,
} from '../../hooks/useSendTelemetry';

const messages = defineMessages({
  linkCaption: {
    id: 'goto.join.troubleshooting.unsupportedBrowser.linkCaption',
  },
  copy: {
    id: 'goto.join.troubleshooting.unsupportedBrowser.copy',
  },
  copied: {
    id: 'goto.join.troubleshooting.unsupportedBrowser.copied',
  },
  googleChrome: {
    id: 'goto.join.troubleshooting.unsupportedBrowser.googleChrome',
  },
  microsoftEdge: {
    id: 'goto.join.troubleshooting.unsupportedBrowser.microsoftEdge',
  },
  instruction: {
    id: 'goto.unsupported.instruction',
  },
});

export const TroubleshootingUnsupportedBrowser: React.FC = () => {
  const { formatMessage: f } = useIntl();
  const currentUrl = getCurrentUrl();
  const sendTelemetry = useSendTelemetry();
  const copyClick = () => {
    onCopy();
    sendTelemetry(
      TelemetryComponent.Web,
      TelemetryAction.Copy,
      TelemetryState.Unsupported
    );
  };
  const { hasCopied, onCopy } = useClipboard(currentUrl);
  return (
    <>
      <GoToText fontSize='sm'>
        {f(messages.instruction, {
          chrome: (
            <GoToLink fontSize='sm' href={installGoogleChromeUrl} isExternal>
              {f(messages.googleChrome)}
            </GoToLink>
          ),
          edge: (
            <GoToLink fontSize='sm' href={installMicrosoftEdgeUrl} isExternal>
              {f(messages.microsoftEdge)}
            </GoToLink>
          ),
        })}
      </GoToText>

      <Flex marginTop={Spacing.CoreLayout1} maxWidth='30rem'>
        <TextField
          size='sm'
          height={Spacing.CoreLayout2}
          textOverflow='ellipsis'
          value={currentUrl}
          isReadOnly
        />
        <StyledButton
          variant='outline'
          size='sm'
          paddingX={Spacing.CoreLayout1}
          height='100%'
          onClick={copyClick}
          leftIcon={
            hasCopied ? (
              <CheckIcon
                height={Spacing.CoreLayout1}
                width={Spacing.CoreLayout1}
                color='type-color-brand-default'
              />
            ) : (
              <CopyActiveIcon
                height={Spacing.CoreLayout1}
                width={Spacing.CoreLayout1}
                color='type-color-brand-default'
              />
            )
          }
          marginLeft={Spacing.XSmall}
        >
          {hasCopied ? f(messages.copied) : f(messages.copy)}
        </StyledButton>
      </Flex>
    </>
  );
};
